import { Component, ViewChild } from '@angular/core'
import { MaintenanceDto } from '../../shared/model/maintenance.model'
import { MaintenanceService } from '../../shared/service/maintenance.service'
import { filterOkMapBody } from '../../shared/util/http.utils'
import moment from 'moment'
import { convertToHumanDate } from '../../shared/util/moment.utils'
import { MaintenanceLogErrors } from '../../shared/model/maintenance-log-errors.model'
import { MatSort, Sort, SortDirection } from '@angular/material/sort'
import { FormBuilder } from '@angular/forms'
import { BatchSynchronisation } from '../../shared/model/batch-synchronisation.model'
import { MaintenanceBatchSynchronisation } from '../../shared/model/maintenance-batch-synchronisation.model'
import { BATCH_SYNCHRONISATION_ETAT } from '../../shared/constant/constant'
import { BatchSynchronisationCategorieEnum, BatchSynchronisationEtatEnum } from '../../shared/constant/enum'
import { SnackBarService } from '../../shared/service/snackbar.service'

@Component({
    selector: 'ddc-administration-maintenance',
    templateUrl: './administration-maintenance.component.html',
    styleUrls: ['administration-maintenance.component.scss'],
    standalone: false
})

export class AdministrationMaintenanceComponent {

  isMaintenanceActive: boolean
  maintenanceMessage: string
  structuresSync: BatchSynchronisation
  adherentsSync: BatchSynchronisation
  notificationMailsSync: BatchSynchronisation
  logErrors: MaintenanceLogErrors[]
  sortBy = 'dateCreation';
  sortDirection: SortDirection = 'desc';
    logErrorDisplayedColumns = ['categorie', 'dateCreation', 'message', 'adherent'];
  expandedElement: MaintenanceLogErrors | null;
  isLoading = false;
  retryTimeouts = new Map<BatchSynchronisationCategorieEnum, ReturnType<typeof setTimeout>>();

    BATCH_SYNCHRONISATION_ETAT = BATCH_SYNCHRONISATION_ETAT
    convertToHumanDate = convertToHumanDate;

  @ViewChild(MatSort, {static: true}) sort: MatSort;

  filterForm = this.fb.group({
                               dateDebut: [moment().startOf('day')],
                               dateFin: [moment().endOf('day')]
                             });

  constructor(
    protected maintenanceService: MaintenanceService,
    private fb: FormBuilder,
    protected snackBarService: SnackBarService,
  ) {
  }

  isExpanded(element: MaintenanceLogErrors) {
    return this.expandedElement === element;
  }

  /** Toggles the expanded state of an element, but not when text is being selected. */
  toggle(element: MaintenanceLogErrors) {
    const selection = window.getSelection()?.toString();

    if (selection && selection.length > 0) {
      return;
    }

    this.expandedElement = this.isExpanded(element) ? null : element;
  }

  truncate(text: string, limit = 80): string {
    return text.length > limit
           ? text.substring(0, limit) + '...'
           : text;
  }

  updateMaintenance(){
    const maintenanceDto = new MaintenanceDto(this.isMaintenanceActive, this.maintenanceMessage)
    this.maintenanceService.updateMaintenanceInfos(maintenanceDto)
  }

  syncStructures(){
    this.isLoading = true;
    this.maintenanceService
        .syncStructures()
        .subscribe({
                     next: (data: BatchSynchronisation) => {
                       this.structuresSync = data;
                       this.isLoading = false;
                       const retryTimeout = setTimeout(() => {
                         this.getBatchByCategorie(BatchSynchronisationCategorieEnum.SYNCHRONISATION_STRUCTURES);
                       }, 30000);
                       this.retryTimeouts.set(BatchSynchronisationCategorieEnum.SYNCHRONISATION_STRUCTURES, retryTimeout);
                       },
                     error: () => {
                       this.isLoading = false;
                     }
        });
  }

    syncAdherents(){
      this.isLoading = true
      this.maintenanceService.syncAdherents()
          .subscribe({
                       next: (data: BatchSynchronisation) => {
                         this.adherentsSync = data;
                         this.isLoading = false;
                         const retryTimeout = setTimeout(() => {
                           this.getBatchByCategorie(BatchSynchronisationCategorieEnum.SYNCHRONISATION_INFOS_ADHERENT);
                         }, 30000);
                         this.retryTimeouts.set(BatchSynchronisationCategorieEnum.SYNCHRONISATION_INFOS_ADHERENT, retryTimeout);
                       },
                       error: () => {
                         this.isLoading = false;
                      }
          });
    }

    sendNotifications(){
      this.isLoading = true
      this.maintenanceService.syncNotifications()
          .subscribe({
                       next: (data: BatchSynchronisation) => {
                         this.notificationMailsSync = data;
                         this.isLoading = false;
                         const retryTimeout = setTimeout(() => {
                           this.getBatchByCategorie(BatchSynchronisationCategorieEnum.SYNCHRONISATION_MAILS_NOTIFICATIONS);
                         }, 30000);
                         this.retryTimeouts.set(BatchSynchronisationCategorieEnum.SYNCHRONISATION_MAILS_NOTIFICATIONS, retryTimeout);
                       },
                       error: () => {
                         this.isLoading = false;
                       }
          });
    }

  getMaintenanceLogErrors(dateDebut: string, dateFin: string) {
      this.maintenanceService.getMaintenanceLogErrors(dateDebut, dateFin, this.sortBy, this.sortDirection)
          .pipe(filterOkMapBody)
          .subscribe((data: MaintenanceLogErrors[]) => {
            this.logErrors = data;
          });
    }

  searchLogErrors() {
    const fromDate = this.filterForm.value.dateDebut;
    const toDate = this.filterForm.value.dateFin.endOf('day');

    this.getMaintenanceLogErrors(fromDate.toISOString(), toDate.toISOString());
  }

  onSort(sort: Sort) {
    this.sortBy = sort.active;
    this.sortDirection = sort.direction;
    this.searchLogErrors();
  }

  getBatchByCategorie(categorie: BatchSynchronisationCategorieEnum) {
    this.maintenanceService.getBatchInfosByCategorie(categorie).pipe(filterOkMapBody).subscribe((data: BatchSynchronisation) => {
      if (data?.etat === BatchSynchronisationEtatEnum.EN_COURS) {
        const retryTimeout = setTimeout(() => {
          this.getBatchByCategorie(categorie);
        }, 60000);
        this.retryTimeouts.set(categorie, retryTimeout);
        return;
      }
      switch (categorie) {
        case BatchSynchronisationCategorieEnum.SYNCHRONISATION_STRUCTURES:
          this.structuresSync = data;
          this.getSnackBarMessageBySynchronisationState('La synchronisation des structures s\'est terminée avec l\'état : ', data?.etat);
          break;
        case BatchSynchronisationCategorieEnum.SYNCHRONISATION_INFOS_ADHERENT:
          this.adherentsSync = data;
          this.getSnackBarMessageBySynchronisationState('La synchronisation des informations des adhérents s\'est terminée avec l\'état : ', data?.etat);
          break;
        case BatchSynchronisationCategorieEnum.SYNCHRONISATION_MAILS_NOTIFICATIONS:
          this.notificationMailsSync = data;
          this.getSnackBarMessageBySynchronisationState('La synchronisation de l\'envoie des mails de notification s\'est terminée avec l\'état : ', data?.etat);
      }
      this.searchLogErrors();
    });
  }

  getSnackBarMessageBySynchronisationState(message: string, etat: BatchSynchronisationEtatEnum) {
    if (etat === BatchSynchronisationEtatEnum.TERMINE) {
      return this.snackBarService.showSuccess(message + BATCH_SYNCHRONISATION_ETAT[etat], {durationInSeconds: 2});
    }
    return this.snackBarService.showWarning(message + BATCH_SYNCHRONISATION_ETAT[etat], {durationInSeconds: 2});
  }

  ngOnDestroy(): void {
    if (this.retryTimeouts) {
      this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
      this.retryTimeouts.clear();
    }
  }

  ngOnInit(): void {
    this.maintenanceService.getMaintenanceInfos().pipe(filterOkMapBody).subscribe((data: MaintenanceDto) => {
      this.isMaintenanceActive = data.isActive
      this.maintenanceMessage = data.message
    })

    this.maintenanceService.getBatchInfos().pipe(filterOkMapBody).subscribe((data: MaintenanceBatchSynchronisation) => {
        this.structuresSync = data.structuresSynchronisation
        this.adherentsSync = data.adherentSynchronisation
        this.notificationMailsSync = data.notificationsSynchronisation
    })

    this.getMaintenanceLogErrors(moment().startOf('day').toISOString(),
                                 moment().endOf('day').toISOString());
  }
}
